<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { LayerCake, Svg } from "layercake";
  import { scaleBand, scaleOrdinal } from "d3-scale";
  import { BasicDropdown, AxisX, AxisY, Tooltip } from "@urbaninstitute/dataviz-components";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import Line from "$lib/components/Chart/Line.svelte";
  import Voronoi from "$lib/components/Chart/Voronoi.svelte";
  import { getDomainMax } from "$utils/charts.js";
  import { getStateName } from "$utils/states.js";
  import { formatTextTemplate } from "$utils/text.js";
  import { FORMATTERS, FORMAT } from "$utils/formatters.js";
  import { TRANSIT_MODES, TRANSIT_MODES_DROPDOWN } from "$utils/consts.js";

  const { parsedData, currentState } = $props();

  const YEARS = ["1980-01-01", "1990-01-01", "2000-01-01", "2010-01-01", "2022-01-01"];
  const COLORS = [urbanColors.blue, urbanColors.blue_shade_darker];
  const LABELS = ["Total housing units", "Affordable housing units"];
  const legendScale = scaleOrdinal().domain(LABELS).range(COLORS);
  const fmt = FORMATTERS[FORMAT.NUMBER_ABBR];

  let mode = $state("bus_all_15");
  let activeIndex = $state(null);
  let voronoiActivePoint = $state(null);
  let circleEl = $state(null);

  const timeSeriesData = $derived.by(() => {
    if (!parsedData || !currentState) return [];
    const timeSeries = parsedData.getTimeSeries(currentState);
    const hsgCol = `key_num_hsg_${mode}_half`;
    const nhpdCol = `key_num_hsg_${mode}_half_nhpd`;
    return timeSeries
      .filter((r) => YEARS.includes(/** @type {string} */ (r.date)))
      .map((r) => ({
        date: r.date,
        dateLabel: parsedData.formatDate(/** @type {string} */ (r.date)),
        housing_units: r[hsgCol],
        affordable_units: r[nhpdCol]
      }));
  });

  const affordableData = $derived(
    timeSeriesData.map((d) => ({
      ...d,
      housing_units: d.affordable_units
    }))
  );

  const yMax = $derived.by(() => {
    const vals = timeSeriesData.flatMap((d) =>
      [d.housing_units, d.affordable_units].filter((v) => v != null)
    );
    return getDomainMax(vals, { allZeroFallback: 10 });
  });

  const voronoiPoints = $derived.by(() => {
    const pts = [];
    const modeLabel = TRANSIT_MODES?.[mode]?.toLowerCase() || "";
    for (const d of timeSeriesData) {
      if (d.housing_units != null) {
        pts.push({
          dateLabel: d.dateLabel,
          value: d.housing_units,
          seriesKey: "housing_units",
          label: `${LABELS[0]}`
        });
      }
      if (d.affordable_units != null) {
        pts.push({
          dateLabel: d.dateLabel,
          value: d.affordable_units,
          seriesKey: "affordable_units",
          label: `${LABELS[1]}`
        });
      }
    }
    return pts;
  });

  const activePointData = $derived(
    activeIndex != null && voronoiPoints[activeIndex] ? voronoiPoints[activeIndex] : null
  );

  const stateName = $derived(currentState ? getStateName(currentState) : "");

  function roundPercent(raw) {
    //round to nearest unit
    return Math.round(raw * 100);
  }

  const textStats = $derived.by(() => {
    const stateData = parsedData
      .getPrimaryData()
      .find(({ map_id, date }) => map_id === currentState && date === null);
    const fmtPct = FORMATTERS[FORMAT.PERCENT];
    const change_bus_all =
      stateData?.key_change_num_bus_all_housing_units_2000_2020 != null
        ? stateData.key_change_num_bus_all_housing_units_2000_2020
        : null;
    const change_total =
      stateData?.key_change_total_housing_units_2000_2020 != null
        ? stateData.key_change_total_housing_units_2000_2020
        : null;

    const changeDir =
      change_bus_all !== null && change_total !== null
        ? roundPercent(change_total) === roundPercent(change_bus_all)
          ? "about the same as"
          : change_total > change_bus_all
            ? "lower than"
            : "higher than"
        : null;
    return {
      state: stateName,
      change_dir: changeDir,
      key_change_num_bus_all_housing_units_2000_2020: fmtPct(change_bus_all).replace("%", ""),
      key_change_total_housing_units_2000_2020: fmtPct(change_total).replace("%", "")
    };
  });
</script>

{#if currentState}
  <h5 class="section-title">
    How has housing supply changed in areas within a half mile of transit in {stateName}?
  </h5>
  <p class="section-description">
    Nationwide, the number of housing units within a half mile of any bus or rail transit increased
    by 36 percent from 2000 to 2022 in the median urban area. That’s higher than the increase in the
    number of all housing units nationwide (11 percent) over the same period.
  </p>
  <p class="section-description">
    {formatTextTemplate(
      "In [state], the number of housing units within a half mile of any bus or rail transit increased by [key_change_num_bus_all_housing_units_2000_2020] percent from 2000 to 2022. That’s [change_dir] the increase in the number of all housing units in the state ([key_change_total_housing_units_2000_2020] percent) over the same period.",
      textStats
    )}
  </p>
  <div class="dropdown-row">
    <BasicDropdown
      data={TRANSIT_MODES_DROPDOWN}
      bind:value={mode}
      inlineLabel="Select transit mode"
      id="section-d-mode"
      placeholder={null}
    />
  </div>
  <p class="chart-label">
    Number of housing units within a half mile of {TRANSIT_MODES?.[mode]?.toLowerCase()}, 1980 to
    2022
  </p>
  <ColorLegend scale={legendScale} swatch={true} />
  {#if timeSeriesData.length > 0}
    <div class="chart-container">
      <LayerCake
        padding={{ top: 10, right: 20, bottom: 30, left: 55 }}
        x="dateLabel"
        y="housing_units"
        xScale={scaleBand().paddingInner(1).paddingOuter(0.2)}
        yDomain={[0, yMax]}
        yNice={true}
        data={timeSeriesData}
      >
        <Svg>
          <AxisX />
          <AxisY formatTick={(d) => fmt(d)} ticks={4} gridlines />
          <Line stroke={COLORS[0]} />
          <Line stroke={COLORS[1]} data={affordableData} />
          <Voronoi points={voronoiPoints} bind:activeIndex bind:activePoint={voronoiActivePoint} />
          {#if voronoiActivePoint}
            <circle
              bind:this={circleEl}
              r="5"
              cx={voronoiActivePoint.x}
              cy={voronoiActivePoint.y}
              fill={voronoiActivePoint.seriesKey === "housing_units" ? COLORS[0] : COLORS[1]}
              stroke={voronoiActivePoint.seriesKey === "housing_units" ? COLORS[0] : COLORS[1]}
              stroke-width="3"
            />
          {/if}
        </Svg>
      </LayerCake>
      {#if voronoiActivePoint && activePointData && circleEl}
        <Tooltip el={circleEl}>
          <h5>{activePointData.dateLabel}</h5>
          <p>{activePointData.label}: {fmt(activePointData.value)}</p>
        </Tooltip>
      {/if}
    </div>
  {:else}
    <p class="placeholder-text">No time series data available for this selection.</p>
  {/if}
{:else}
  <p class="placeholder-text">Select a state to see housing supply trends near transit.</p>
{/if}

<style>
  .section-title {
    margin-block-start: 0;
  }
  .dropdown-row {
    margin-bottom: var(--spacing-4);
  }
  .placeholder-text {
    color: var(--color-gray-shade-dark);
  }
  .chart-container {
    width: 100%;
    height: 350px;
    position: relative;
  }
</style>
